import { API_URL } from '../config';

/** Эндпоинты входа: 401 здесь — неверные данные, а не истёкший JWT */
const PUBLIC_AUTH_ENDPOINTS = new Set(['/login-password', '/telegram-auth']);

async function readErrorMessage(response, fallback) {
  try {
    const data = await response.json();
    return data.message || data.error || fallback;
  } catch {
    return fallback;
  }
}

class ApiService {
  constructor() {
    this.token = null;
  }

  setToken(token) {
    this.token = token;
  }

  async request(endpoint, options = {}) {
    const { timeoutMs, ...fetchOptions } = options;

    const headers = {
      ...(this.token && { Authorization: `Bearer ${this.token}` }),
      ...fetchOptions.headers,
    };

    // Don't set Content-Type for FormData, let the browser set it with the boundary
    if (!(fetchOptions.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const controller = typeof timeoutMs === 'number' && timeoutMs > 0 ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

    let response;
    try {
      response = await fetch(`${API_URL}${endpoint}`, {
        ...fetchOptions,
        headers,
        ...(controller ? { signal: controller.signal } : {}),
      });
    } catch (e) {
      if (e.name === 'AbortError') {
        throw new Error('Превышено время ожидания ответа сервера. Проверьте сеть.');
      }
      throw e;
    } finally {
      if (timer) clearTimeout(timer);
    }

    // Handle authentication errors
    if (response.status === 401 || response.status === 403) {
      const isPublicAuth = PUBLIC_AUTH_ENDPOINTS.has(endpoint);
      if (isPublicAuth) {
        throw new Error(await readErrorMessage(response, 'Ошибка входа'));
      }

      console.warn('Authentication error, attempting to refresh token...');
      
      // Clear web auth token if we're outside Telegram - user needs to login again
      if (!(window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData)) {
        localStorage.removeItem('webAuthToken');
        this.setToken(null);
      }
      
      // Try to refresh token if we're in Telegram
      if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.initData) {
        try {
          const refreshResponse = await this.authenticateTelegram(window.Telegram.WebApp.initData);
          this.setToken(refreshResponse.token);
          
          // Retry the original request with new token
          const retryHeaders = {
            ...(this.token && { Authorization: `Bearer ${this.token}` }),
            ...fetchOptions.headers,
          };
          
          if (!(fetchOptions.body instanceof FormData)) {
            retryHeaders['Content-Type'] = 'application/json';
          }
          
          const retryResponse = await fetch(`${API_URL}${endpoint}`, {
            ...fetchOptions,
            headers: retryHeaders,
          });
          
          if (!retryResponse.ok) {
            throw new Error(`API request failed after token refresh: ${retryResponse.statusText}`);
          }
          
          return retryResponse.json();
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          // Emit event for app to handle re-authentication
          window.dispatchEvent(new CustomEvent('token-expired'));
          throw new Error('Authentication failed. Please refresh the page.');
        }
      } else {
        // In development mode, just throw the error
        throw new Error('Authentication failed. Please refresh the page.');
      }
    }

    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `API request failed: ${response.statusText}`));
    }

    return response.json();
  }

  // Auth
  async authenticateTelegram(initData, botId) {
    console.log('🔍 API DEBUG - authenticateTelegram called with:', { botId, hasInitData: !!initData });
    const data = await this.request('/telegram-auth', {
      method: 'POST',
      body: JSON.stringify({ initData, botId }),
      timeoutMs: 25000,
    });
    this.setToken(data.token);
    return data;
  }

  async authenticateWithPassword(login, password) {
    this.setToken(null);
    localStorage.removeItem('webAuthToken');
    const data = await this.request('/login-password', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
      timeoutMs: 20000,
    });
    this.setToken(data.token);
    return data;
  }

  async setWebCredentials(login, password) {
    return this.request('/user/web-credentials', {
      method: 'PUT',
      body: JSON.stringify({ login, password }),
    });
  }

  // User
  async getUserData() {
    return this.request('/user', { timeoutMs: 20000 });
  }

  async getParticipationSettings(teamId) {
    return this.request(`/user/participation-settings?teamId=${teamId}`);
  }

  async updateParticipationSettings(settings, teamId) {
    return this.request('/user/participation', {
      method: 'PUT',
      body: JSON.stringify({ ...settings, teamId }),
    });
  }

  // Subscription Payment System
  async getSubscriptionStatus() {
    return this.request('/user/subscription-status');
  }


  async recordPayment(playerId, paymentData) {
    return this.request(`/players/${playerId}/payment`, {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async getPlayerPaymentHistory(playerId) {
    return this.request(`/players/${playerId}/payment-history`);
  }

  // User payment registration
  async registerUserPayment(paymentData) {
    return this.request('/user/payment-registration', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async getUserPaymentHistory(teamId) {
    if (teamId) {
      return this.request(`/user/teams/${teamId}/payment-history`);
    }
    return this.request('/user/payment-history');
  }

  // Subscription Notifications
  async getSubscriptionNotificationStats() {
    return this.request('/admin/subscription-notifications/stats');
  }

  async sendTestSubscriptionNotification(telegramId, teamName, amount, deadlineDay) {
    return this.request('/admin/subscription-notifications/test', {
      method: 'POST',
      body: JSON.stringify({
        telegramId,
        teamName,
        amount,
        deadlineDay
      })
    });
  }

  async getSubscriptionNotificationPlayers(teamId) {
    return this.request(`/admin/subscription-notifications/players?teamId=${teamId}`);
  }

  async sendSubscriptionNotifications(teamId) {
    return this.request('/admin/subscription-notifications/send', {
      method: 'POST',
      body: JSON.stringify({ teamId })
    });
  }

  // Admin payment status management
  async updatePlayerPaymentStatus(playerId, paymentData) {
    // Use new endpoint if payment_type_id is provided
    if (paymentData.payment_type_id) {
      return this.request(`/admin/players/${playerId}/payment-status-new`, {
        method: 'PUT',
        body: JSON.stringify(paymentData),
      });
    }
    // Fallback to old endpoint for backward compatibility
    return this.request(`/admin/players/${playerId}/payment-status`, {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }

  async getPlayerPaymentStatus(playerId, paymentMonth) {
    return this.request(`/admin/players/${playerId}/payment-status/${paymentMonth}`);
  }

  async getTeamPlayersPaymentStatuses(teamId, paymentMonth) {
    return this.request(`/admin/teams/${teamId}/players-payment-status-new/${paymentMonth}`);
  }

  async getTeamPlayersPaymentStatusesByType(teamId, paymentTypeId, paymentPeriod) {
    return this.request(`/admin/teams/${teamId}/players-payment-status-by-type/${paymentTypeId}/${paymentPeriod}`);
  }

  async getPlayerPayments(teamId, playerId, paymentTypeId, paymentPeriod) {
    let period = paymentPeriod;
    if (!period) {
      period = new Date().toISOString().slice(0, 7);
    }

    const params = new URLSearchParams();
    if (teamId) {
      params.append('teamId', String(teamId));
    }
    if (paymentTypeId) {
      params.append('paymentTypeId', String(paymentTypeId));
    }

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/admin/players/${playerId}/payments/${period}${query}`);
  }

  async deletePayment(paymentId) {
    return this.request(`/admin/payments/${paymentId}`, {
      method: 'DELETE',
    });
  }

  // Teams
  async getUserTeams() {
    return this.request('/user/teams');
  }

  async getPendingTeams() {
    return this.request('/user/pending-teams');
  }

  async searchTeams(query) {
    return this.request(`/teams/search?query=${query}`);
  }

  async joinTeam(teamId) {
    return this.request('/teams/join', {
      method: 'POST',
      body: JSON.stringify({ teamId }),
    });
  }

  async getPendingRequests(teamId) {
    return this.request(`/teams/${teamId}/pending-requests`);
  }

  async approveRequest(teamId, requestId) {
    return this.request(`/teams/${teamId}/requests/${requestId}/accept`, {
      method: 'POST',
    });
  }

  async rejectRequest(teamId, requestId) {
    return this.request(`/teams/${teamId}/requests/${requestId}/reject`, {
      method: 'POST',
    });
  }

  async generateWebCredentials(teamId, options = {}) {
    return this.request(`/teams/${teamId}/players/generate-credentials`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async resendWebCredentialsTelegram(teamId, options = {}) {
    return this.request(`/teams/${teamId}/players/resend-web-credentials`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async resetWebCredentials(teamId, userId) {
    return this.request(`/teams/${teamId}/players/reset-web-credentials`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });
  }

  async createTeam(teamData) {
    return this.request('/teams', {
      method: 'POST',
      body: JSON.stringify(teamData),
    });
  }

  // Events
  async getTeamEvents(teamId) {
    const events = await this.request(`/events/${teamId}`);
    const eventsWithStats = await Promise.all(
      events.map(async (event) => {
        const stats = await this.request(`/events/${event.id}/stats`);
        return { ...event, stats };
      })
    );
    return eventsWithStats;
  }

  async getTeamEventsWithBulkStats(teamId) {
    const events = await this.request(`/events/${teamId}`);
    
    if (events.length === 0) {
      return events;
    }

    // Get all event IDs
    const eventIds = events.map(event => event.id).join(',');
    
    // Get bulk statistics
    const statsMap = await this.request(`/events/team/${teamId}/stats?eventIds=${eventIds}`);
    
    // Merge events with their statistics
    return events.map(event => ({
      ...event,
      stats: statsMap[event.id] || { total: 0, participating: 0, not_participating: 0, unknown: 0 }
    }));
  }

  async getEventPlayers(eventId) {
    return this.request(`/events/${eventId}/players`);
  }

  async updatePlayerPosition(eventId, playerId, data) {
    return this.request(`/events/${eventId}/players/${playerId}/position`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePlayerColors(eventId, color, fieldId = null) {
    return this.request(`/events/${eventId}/players/update-colors`, {
      method: 'POST',
      body: JSON.stringify({ color, fieldId }),
    });
  }

  async createEvent(eventData) {
    return this.request('/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  }

  async deleteEvent(eventId) {
    return this.request(`/events/${eventId}`, {
      method: 'DELETE',
    });
  }

  async notifyUnknownPlayers(eventId) {
    return this.request(`/events/${eventId}/notify-unknown`, {
      method: 'POST',
    });
  }

  // User Profile
  async uploadPhoto(formData) {
    return this.request('/user/photo', {
      method: 'POST',
      body: formData,
    });
  }

  async uploadReceipt(formData) {
    return this.request('/user/receipt', {
      method: 'POST',
      body: formData,
    });
  }

  async updateProfile(profileData) {
    return this.request('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async updatePlayerProfile(teamId, playerId, playerData) {
    return this.request(`/teams/${teamId}/players/${playerId}`, {
      method: 'PUT',
      body: JSON.stringify(playerData),
    });
  }

  // Team Invitations
  async createTeamInvitation(teamId, invitationData) {
    return this.request(`/teams/${teamId}/invitations`, {
      method: 'POST',
      body: JSON.stringify(invitationData),
    });
  }

  async getTeamInvitations(teamId) {
    return this.request(`/teams/${teamId}/invitations`);
  }

  async deactivateTeamInvitation(teamId, invitationId) {
    return this.request(`/teams/${teamId}/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  async useInvitation(inviteCode) {
    return this.request(`/invitations/${inviteCode}/use`, {
      method: 'POST',
    });
  }

  // Best Player Voting
  async getBestPlayerVotingSettings(teamId) {
    return this.request(`/teams/${teamId}/best-player-voting/settings`);
  }

  async updateBestPlayerVotingSettings(teamId, enabled) {
    return this.request(`/teams/${teamId}/best-player-voting/settings`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async getBestPlayerVotingPolls(teamId) {
    return this.request(`/teams/${teamId}/best-player-voting/polls`);
  }

  async createBestPlayerVotingPoll(teamId, data) {
    return this.request(`/teams/${teamId}/best-player-voting/polls`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getBestPlayerVotingPoll(teamId, pollId) {
    return this.request(`/teams/${teamId}/best-player-voting/polls/${pollId}`);
  }

  async voteBestPlayer(teamId, pollId, playerId) {
    return this.request(`/teams/${teamId}/best-player-voting/polls/${pollId}/vote`, {
      method: 'POST',
      body: JSON.stringify({ player_id: playerId }),
    });
  }

  async getBestPlayerVotingResults(teamId, pollId) {
    return this.request(`/teams/${teamId}/best-player-voting/polls/${pollId}/results`);
  }

  async closeBestPlayerVotingPoll(teamId, pollId) {
    return this.request(`/teams/${teamId}/best-player-voting/polls/${pollId}/close`, {
      method: 'PUT',
    });
  }

  // Admin Panel (Telegram)
  async getAdminStats() {
    return this.request('/admin/stats');
  }

  async getAdminUsers(page = 1, limit = 20, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    return this.request(`/admin/users?${params}`);
  }

  async getAdminTeams(page = 1, limit = 20, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    return this.request(`/admin/teams?${params}`);
  }

  async getAdminSystem() {
    return this.request('/admin/system');
  }

  async getAdminLogs() {
    return this.request('/admin/logs');
  }

  // Separate Admin Panel (Login/Password)
  async getAdminPanelStats() {
    return this.request('/admin-panel/stats', {
      headers: {
        'Admin-Token': localStorage.getItem('adminToken')
      }
    });
  }

  async getAdminPanelUsers(page = 1, limit = 20, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    return this.request(`/admin-panel/users?${params}`, {
      headers: {
        'Admin-Token': localStorage.getItem('adminToken')
      }
    });
  }

  async getAdminPanelTeams(page = 1, limit = 20, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    return this.request(`/admin-panel/teams?${params}`, {
      headers: {
        'Admin-Token': localStorage.getItem('adminToken')
      }
    });
  }

  async getAdminPanelSystem() {
    return this.request('/admin-panel/system', {
      headers: {
        'Admin-Token': localStorage.getItem('adminToken')
      }
    });
  }

  async getAdminPanelPlayers(page = 1, limit = 20, search = '', team = '', position = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(team && { team }),
      ...(position && { position })
    });
    return this.request(`/admin-panel/players?${params}`, {
      headers: {
        'Admin-Token': localStorage.getItem('adminToken')
      }
    });
  }

  // Payment Types Management (Admin)
  async getAdminTeamPaymentTypes(teamId) {
    return this.request(`/admin/teams/${teamId}/payment-types`);
  }

  async createPaymentType(teamId, data) {
    return this.request(`/admin/teams/${teamId}/payment-types`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePaymentType(teamId, typeId, data) {
    return this.request(`/admin/teams/${teamId}/payment-types/${typeId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePaymentType(teamId, typeId) {
    return this.request(`/admin/teams/${teamId}/payment-types/${typeId}`, {
      method: 'DELETE',
    });
  }

  // Player Payments Management
  async getPlayerPaymentStatus(teamId, playerId) {
    return this.request(`/admin/teams/${teamId}/players/${playerId}/payment-status`);
  }

  // Get all team payments (for admin management)
  async getTeamAllPayments(teamId) {
    return this.request(`/admin/teams/${teamId}/all-payments`);
  }

  async createPlayerPayment(teamId, playerId, data) {
    return this.request(`/admin/teams/${teamId}/players/${playerId}/payments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePlayerPayment(teamId, paymentId, data) {
    return this.request(`/admin/teams/${teamId}/payments/${paymentId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePlayerPayment(teamId, paymentId) {
    return this.request(`/admin/teams/${teamId}/payments/${paymentId}`, {
      method: 'DELETE',
    });
  }

  // Payment Exemptions Management
  async getPaymentExemptions(teamId, paymentTypeId) {
    return this.request(`/admin/teams/${teamId}/payment-exemptions/${paymentTypeId}`);
  }

  async updatePaymentExemption(teamId, paymentTypeId, data) {
    return this.request(`/admin/teams/${teamId}/payment-exemptions/${paymentTypeId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Request payment exemption (user request)
  async requestPaymentExemption(teamId, paymentTypeId, data) {
    return this.request(`/user/teams/${teamId}/payment-exemption-request/${paymentTypeId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Get user exemption status
  async getUserExemptionStatus(teamId) {
    return this.request(`/user/teams/${teamId}/exemption-status`);
  }

  // Send payment table to Telegram
  async sendPaymentTableToTelegram(teamId, paymentTypeId, paymentPeriod, data) {
    return this.request(`/admin/teams/${teamId}/send-payment-table/${paymentTypeId}/${paymentPeriod}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async sendPaymentReminderNotifications(teamId, paymentTypeId, paymentPeriod, data) {
    return this.request(`/admin/teams/${teamId}/send-payment-reminders/${paymentTypeId}/${paymentPeriod}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ========================================
  // ПУБЛИЧНЫЕ МЕТОДЫ ДЛЯ ИГРОКОВ
  // ========================================

  // Получить активные типы оплат команды (для игроков)
  async getPublicTeamPaymentTypes(teamId) {
    return this.request(`/teams/${teamId}/payment-types`);
  }

  // Зарегистрировать платеж игроком
  async registerPlayerPayment(paymentData) {
    return this.request('/user/player-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  // ==================== PAYMENT ADMINS MANAGEMENT ====================

  // Get payment admins for a specific payment type
  async getPaymentAdmins(teamId, paymentTypeId) {
    return this.request(`/admin/teams/${teamId}/payment-types/${paymentTypeId}/admins`);
  }

  // Add payment admin
  async addPaymentAdmin(teamId, paymentTypeId, playerId) {
    return this.request(`/admin/teams/${teamId}/payment-types/${paymentTypeId}/admins`, {
      method: 'POST',
      body: JSON.stringify({ playerId }),
    });
  }

  // Remove payment admin
  async removePaymentAdmin(teamId, paymentTypeId, adminId) {
    return this.request(`/admin/teams/${teamId}/payment-types/${paymentTypeId}/admins/${adminId}`, {
      method: 'DELETE',
    });
  }

  // Check if user can manage specific payment type
  async canManagePaymentType(teamId, paymentTypeId) {
    return this.request(`/teams/${teamId}/payment-types/${paymentTypeId}/can-manage`);
  }

  // Get team players
  async getTeamPlayers(teamId) {
    return this.request(`/teams/${teamId}/players`);
  }
}

export const api = new ApiService(); 